import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Input, Button, Card, Empty, Spin, Modal, Pagination, message } from 'antd';
import { SearchOutlined, DownloadOutlined, LeftOutlined, RightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { downloadImageFile, imageLibrary, proxyImageUrl } from '../../api';
import './ImageLibrary.css';

const { Header, Content } = Layout;

function ImageLibrary() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 12, total: 0 });
  const [searchValue, setSearchValue] = useState(searchParams.get('keyword') || '');
  const keyword = searchParams.get('keyword') || '';

  useEffect(() => {
    setSearchValue(keyword);
  }, [keyword]);

  useEffect(() => {
    fetchData();
  }, [keyword, pagination.current, pagination.pageSize]);

  const currentPreviewItem = useMemo(() => data[previewIndex] || null, [data, previewIndex]);

  const updateQueryParams = (value) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set('keyword', value);
    } else {
      nextParams.delete('keyword');
    }

    setSearchParams(nextParams);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await imageLibrary.list({
        keyword: keyword || undefined,
        page: pagination.current,
        pageSize: pagination.pageSize
      });
      setData(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));
    } catch (err) {
      setData([]);
      message.error('获取图片库失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    updateQueryParams(value?.trim() || '');
  };

  const handleDownload = async (item) => {
    try {
      await downloadImageFile(item.image_url, item.title);
    } catch (err) {
      message.error(err.message || '下载失败');
    }
  };

  const openPreview = (index) => {
    if (!data[index]) {
      return;
    }

    setPreviewIndex(index);
    setPreviewVisible(true);
  };

  const handlePreviewStep = (direction) => {
    const nextIndex = previewIndex + direction;

    if (nextIndex < 0 || nextIndex >= data.length) {
      return;
    }

    setPreviewIndex(nextIndex);
  };

  return (
    <Layout className="image-library-layout">
      <Header className="image-library-header">
        <div className="image-library-header-left">
          <Button icon={<ArrowLeftOutlined />} className="image-library-back-btn" onClick={() => navigate('/')}>
            返回首页
          </Button>
          <div className="image-library-title-group">
            <h1 className="image-library-title">瑞华图片库</h1>
            <div className="image-library-subtitle">集中查看常用产品图、实拍图和宣传图，支持直接预览和下载。</div>
          </div>
        </div>

        <div className="image-library-search-box">
          <Input
            placeholder="搜索图片标题或说明"
            allowClear
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onPressEnter={() => handleSearch(searchValue)}
            className="image-library-search-input"
          />
          <Button icon={<SearchOutlined />} className="image-library-search-btn" onClick={() => handleSearch(searchValue)}>
            搜索
          </Button>
        </div>
      </Header>

      <Content className="image-library-content">
        <div className="image-library-toolbar">
          共 <strong>{pagination.total}</strong> 张图片
        </div>

        <Spin spinning={loading}>
          {data.length ? (
            <>
              <div className="image-library-grid">
                {data.map((item, index) => (
                <Card
                  key={item.id}
                  hoverable
                  className="image-library-card"
                  bodyStyle={{ padding: 16 }}
                  cover={
                    <div className="image-library-card-cover" onClick={() => openPreview(index)}>
                      <img
                        alt={item.title}
                        src={proxyImageUrl(item.image_url, { width: 880, height: 660, mode: 'lfit', quality: 85 })}
                        className="image-library-card-image"
                      />
                    </div>
                  }
                >
                  <div className="image-library-card-title">{item.title}</div>
                  <div className="image-library-card-desc">{item.description || '暂无说明'}</div>
                  <Button
                    icon={<DownloadOutlined />}
                    className="image-library-download-btn"
                    onClick={() => handleDownload(item)}
                  >
                    下载图片
                  </Button>
                </Card>
                ))}
              </div>

              <div className="image-library-pagination-wrap">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger
                  pageSizeOptions={['12', '24', '36', '48']}
                  showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={(page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current: page,
                      pageSize
                    }));
                  }}
                />
              </div>
            </>
          ) : (
            <div className="image-library-empty">
              <Empty description="当前没有可展示的图片" />
            </div>
          )}
        </Spin>
      </Content>

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        className="image-library-preview-modal"
        width="auto"
      >
        {currentPreviewItem ? (
          <div className="image-library-preview">
            <div className="image-library-preview-stage">
              <Button
                icon={<LeftOutlined />}
                className="image-library-preview-nav"
                disabled={previewIndex <= 0}
                onClick={() => handlePreviewStep(-1)}
              />
              <img
                alt={currentPreviewItem.title}
                src={proxyImageUrl(currentPreviewItem.image_url)}
                className="image-library-preview-image"
              />
              <Button
                icon={<RightOutlined />}
                className="image-library-preview-nav"
                disabled={previewIndex >= data.length - 1}
                onClick={() => handlePreviewStep(1)}
              />
            </div>

            <div className="image-library-preview-meta">
              <div className="image-library-preview-title">{currentPreviewItem.title}</div>
              <div className="image-library-preview-desc">{currentPreviewItem.description || '暂无说明'}</div>
            </div>

            <div className="image-library-preview-actions">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(currentPreviewItem)}
              >
                下载原图
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </Layout>
  );
}

export default ImageLibrary;
